import React from "react";

export const prefersReducedMotion = () => (
  !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
);

//Mouse / trackpad (not touch screens)
export const hasFinePointer = () => (
  !!window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches
);

//Sets --parallax on the element as it scrolls past: -1 when it enters at the bottom of the screen, 1 when it leaves at the top
export const useParallax = () => {
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) {
      return undefined;
    }

    let frame = null;
    const update = () => {
      frame = null;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        return;
      }
      const progress = 1 - ((rect.top + rect.height) / (window.innerHeight + rect.height)) * 2;
      el.style.setProperty("--parallax", progress.toFixed(4));
    };
    const onScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  return ref;
};

//True once the element has scrolled into view (used to fade things in, as on the original site)
export const useRevealOnScroll = () => {
  const ref = React.useRef(null);
  const [isRevealed, setIsRevealed] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window) || prefersReducedMotion()) {
      setIsRevealed(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setIsRevealed(true);
        observer.disconnect();
      }
    }, { threshold: 0.15 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, isRevealed];
};
